import { FeatureTypes, CapabilitiesDocumentation, WorkflowCapabilitiesDocumentation } from '@/components/Billing/billing-types';
const BASE_URL = process.env.VUE_APP_APP_URL;
export const CAPABILITIES: CapabilitiesDocumentation[] = [
	{
		id: FeatureTypes.CustomerCustomDomains,
		title: 'Custom domains for portals',
		description: 'An admin in your workspace has set up a custom domain for hosting customer portals.',
		howToFix:
			'Go to workspace settings and delete all custom domains. Any portals previously hosted at this domain will no longer be usable. Their default Ply URLs, found in the feature details, still work.',
		link: `${BASE_URL}settings/custom-domains`,
		linkText: 'Go to domain settings',
	},
	{
		id: FeatureTypes.Groups,
		title: 'User groups',
		description:
			'An admin in your workspace has created a group of workspace members that you can reference for functions like feature sharing, invitation to spaces, and shared inboxes.',
		howToFix:
			'Go to group settings and delete all groups. Group members will lose access to all of the assets, spaces, and inboxes that were shared to them via the group (unless they independently had access as users).',
		link: `${BASE_URL}settings/organization/groups/`,
		linkText: 'Go to group settings',
	},
	{
		id: FeatureTypes.SyncedGroups,
		title: 'Synced user groups',
		description:
			'An admin in your workspace has created a group of workspace members whose membership is synced with a user list from an external app.',
		howToFix:
			'Go to group settings and delete all synced groups. Group members will lose access to all of the assets, spaces, and inboxes that were shared to them via the group (unless they independently had access as users).',
		link: `${BASE_URL}settings/organization/groups/`,
		linkText: 'Go to group settings',
	},
	{
		id: FeatureTypes.GroupUsagePermissions,
		title: 'Group usage permissions',
		description: 'An admin in your workspace has applied fine-grained permission restrictions to a user group.',
		howToFix: 'Ensure that all uses are allowed for all user groups.',
		link: `${BASE_URL}settings/organization/groups/`,
		linkText: 'Go to group settings',
	},
	{
		id: FeatureTypes.PremiumUsersCount,
		title: 'Excessive premium members',
		description: 'Your premium user count (N) is greater than what is allowed in the selected team features plan.',
		howToFix: 'Reduce your active premium member count to N. You can do this by changing member roles or deactivating members.',
		link: `${BASE_URL}settings/organization/users`,
		linkText: 'Go to membership settings',
	},
	{
		id: FeatureTypes.PremiumUsersCount,
		title: 'Excessive premium members',
		description: 'Your standard user count (N) is greater than what is allowed in the selected team features plan.',
		howToFix: 'Reduce your active standard member count to N. You can do this by changing member roles or deactivating members.',
		link: `${BASE_URL}settings/organization/users`,
		linkText: 'Go to membership settings',
	},
	{
		id: FeatureTypes.CreatorSpacePermissions,
		title: 'Advanced user roles in creator spaces',
		description: 'There are shared creator spaces in your workspace where collaborators have advanced user roles (not just space admin).',
		howToFix: 'Ensure that all collaborators in all shared spaces have the role space admin.',
		link: `${BASE_URL}manage/space/`,
		linkText: 'Go to spaces',
	},
	{
		id: FeatureTypes.UserHierarchy,
		title: 'Advanced workspace roles',
		description: 'There are members in your workspace with advanced creator roles (not just workspace admin).',
		howToFix: 'Ensure that all creators have the workspace role of admin or downgrade them to end users.',
		link: `${BASE_URL}manage/space/`,
		linkText: 'Go to spaces',
	},
	{
		id: FeatureTypes.SensitiveConnections,
		title: 'Sensitive connections in creator spaces',
		description: 'There are shared creator spaces in your workspace with connections that have been set as sensitive.',
		howToFix: 'Ensure that all connections in all shared spaces are accessible to all creators.',
		link: `${BASE_URL}manage/space/`,
		linkText: 'Go to spaces',
	},
	{
		id: FeatureTypes.SharedNotificationInboxes,
		title: 'Shared notification inboxes',
		description: 'A user in your workspace has created a shared notification inbox.',
		howToFix: 'Talk with support to delete the shared inbox.',
	},
];

export const CAPABILITIES_MAP: Record<FeatureTypes, CapabilitiesDocumentation> = CAPABILITIES.reduce((acc, curr) => {
	acc[curr.id] = curr;
	return acc;
}, {} as Record<FeatureTypes, CapabilitiesDocumentation>);

export const WORKFLOW_CAPABILITIES: WorkflowCapabilitiesDocumentation[] = [
	{
		id: FeatureTypes.SubFlows,
		title: 'SubFlows',
		howToFix: 'Disable the feature or republish after deleting subflow steps.',
	},
	{
		id: FeatureTypes.TeamOutgoingApiActions,
		title: 'Custom webhooks and API calls',
		howToFix: 'Disable the feature or republish after deleting custom API actions.',
	},
	{
		id: FeatureTypes.CustomerOutgoingApiActions,
		title: 'Custom webhooks and API calls',
		howToFix: 'Disable the feature or republish after deleting custom API actions.',
	},
	{
		id: FeatureTypes.TeamCustomCodeActions,
		title: 'Javascript code block',
		howToFix: 'Disable the feature or republish after deleting code block steps.',
	},
	{
		id: FeatureTypes.CustomerCustomCodeActions,
		title: 'Javascript code block',
		howToFix: 'Disable the feature or republish after deleting code block steps.',
	},
	{
		id: FeatureTypes.CustomerUrlParameters,
		title: 'URL parameters',
		howToFix: 'Disable the feature or republish after deleting URL parameters from the URL trigger step.',
	},
	{
		id: FeatureTypes.CustomerIframeBlock,
		title: 'Iframe block in an interface step',
		howToFix: 'Disable the feature or republish after deleting all iframe blocks from interfaces.',
	},
	{
		id: FeatureTypes.CustomerMiniApps,
		title: 'Mini-app step',
		howToFix: 'Disable the feature or republish after deleting all mini-app steps.',
	},
	{
		id: FeatureTypes.CustomerRedirect,
		title: 'Portal URL redirect',
		howToFix: 'Disable the feature or republish after deleting all URL redirect steps.',
	},
	{
		id: FeatureTypes.CustomerExtraPremiumApps,
		title: 'Salesforce actions',
		howToFix: 'Disable the feature or republish after deleting all Salesforce steps.',
	},
	{
		id: FeatureTypes.CustomerAuthEmailSignIn,
		title: 'Email authentication',
		howToFix: 'Disable the feature or republish after disabling email authentication.',
	},
	{
		id: FeatureTypes.CustomerAuthSMSSignIn,
		title: 'Email authentication',
		howToFix: 'Disable the feature or republish after disabling SMS authentication.',
	},
];

export const WORKFLOW_CAPABILITIES_MAP: Record<FeatureTypes, WorkflowCapabilitiesDocumentation> = WORKFLOW_CAPABILITIES.reduce(
	(acc, curr) => {
		acc[curr.id] = curr;
		return acc;
	},
	{} as Record<FeatureTypes, WorkflowCapabilitiesDocumentation>
);
